import type { FlightRecord } from '@trips/shared/flight-stats';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';

/**
 * Friends live on the server (each person has their own copy of the app); their flights are
 * fetched through my server and kept on this device, so maps and stats still show offline.
 */

export interface Friend {
  id: string;
  name: string | null;
  origin: string | null;
  status: 'pending' | 'linked';
  shareMap: boolean;
  shareStats: boolean;
  showOnMap: boolean;
  createdAt: number;
  inviteExpires: number | null;
}

export interface FriendFlights {
  name: string;
  /** What they share with me. */
  map: boolean;
  stats: boolean;
  records: FlightRecord[];
}

/** Colours for friends' routes on the map, in order. Mine stay blue. */
export const FRIEND_COLORS = ['#f59e0b', '#ec4899', '#10b981', '#a855f7', '#ef4444'];

const read = <T>(key: string): T | null => {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
};
const write = (key: string, v: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    // Full or private: it just isn't kept.
  }
};

export async function fetchFriends(): Promise<Friend[]> {
  const r = await api<{ friends: Friend[] }>('/api/friends');
  write('trips.friends', r.friends);
  return r.friends;
}

/** Linked friends; the last list known shows straight away, then the fresh one. */
export function useFriends(): [Friend[], () => void] {
  const [list, setList] = useState<Friend[]>(() => read<Friend[]>('trips.friends') ?? []);
  const [n, setN] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: n is the reload trigger
  useEffect(() => {
    void fetchFriends()
      .then(setList)
      .catch(() => {});
  }, [n]);
  return [list, () => setN((x) => x + 1)];
}

/** One friend's shared flights (cached on the device). */
export function useFriendFlights(id: string | null): {
  data: FriendFlights | null;
  error: string | null;
} {
  const [data, setData] = useState<FriendFlights | null>(() =>
    id ? read<FriendFlights>(`trips.friend.${id}`) : null,
  );
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    setData(read<FriendFlights>(`trips.friend.${id}`));
    setError(null);
    let alive = true;
    void api<FriendFlights>(`/api/friends/${encodeURIComponent(id)}/flights`)
      .then((d) => {
        if (!alive) return;
        write(`trips.friend.${id}`, d);
        setData(d);
      })
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : 'Offline'));
    return () => {
      alive = false;
    };
  }, [id]);
  return { data, error };
}

/** Several friends' flights at once, for the map. */
export function useFriendsFlights(ids: string[]): Map<string, FriendFlights> {
  const key = ids.join(',');
  const [all, setAll] = useState<Map<string, FriendFlights>>(
    () =>
      new Map(
        ids.flatMap((id) => {
          const d = read<FriendFlights>(`trips.friend.${id}`);
          return d ? [[id, d] as const] : [];
        }),
      ),
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the id list
  useEffect(() => {
    let alive = true;
    for (const id of ids)
      void api<FriendFlights>(`/api/friends/${encodeURIComponent(id)}/flights`)
        .then((d) => {
          write(`trips.friend.${id}`, d);
          if (alive) setAll((m) => new Map(m).set(id, d));
        })
        .catch(() => {});
    return () => {
      alive = false;
    };
  }, [key]);
  return all;
}
